// src/components/Navbar/Navbar.jsx
import React, { useContext, useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../Context/UserContext";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { clearToken, isImpersonating } from "../../utils/authToken";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";
import { FaBars, FaTimes } from "react-icons/fa";
import { GrLanguage } from "react-icons/gr";
import InstallAppButton from "../InstallAppButton";
import "./Navbar.css";

export default function Navbar({ toggleSidebar, isSidebarOpen }) {
  const { Authorization, setAuthorization, hydrateFarmAuth } =
    useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // اللغة المحفوظة
  const savedLang = localStorage.getItem("lang") || i18n.language;
  const [language, setLanguage] = useState(savedLang);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const userRole = Authorization ? jwtDecode(Authorization).role : null;
  const isAdmin =
    !isImpersonating() &&
    (userRole === "admin" || localStorage.getItem("isAdmin") === "true");

  // Get user name from profile API or JWT token
  const { data: profileData } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      // Try multiple endpoint patterns
      const endpoints = [
        "/auth/profile",
        "/users/me",
        "/user/me",
        "/auth/me",
        "/me"
      ];
      
      for (const endpoint of endpoints) {
        try {
          const res = await axiosInstance.get(endpoint);
          if (res.data?.data?.profile) {
            return res.data.data.profile;
          } else if (res.data?.data) {
            return res.data.data;
          } else if (res.data?.profile) {
            return res.data.profile;
          } else if (res.data) {
            return res.data;
          }
        } catch (err) {
          if (err.response?.status !== 404) {
            throw err;
          }
        }
      }
      return null;
    },
    enabled: !!Authorization, // Only fetch if user is logged in
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Refetch when component mounts to get latest name
  });

  useEffect(() => {
    if (profileData) hydrateFarmAuth(profileData);
  }, [profileData, hydrateFarmAuth]);

  // Get user name from profile data or JWT token
  const userName = useMemo(() => {
    // Always prioritize profileData name when available
    if (profileData?.name) {
      return profileData.name;
    }
    // Fallback to JWT token name only (never use email)
    if (Authorization) {
      try {
        const decoded = jwtDecode(Authorization);
        // Only return decoded.name, never use email as fallback
        return decoded.name || null;
      } catch (error) {
        return null;
      }
    }
    return null;
  }, [profileData, Authorization]);

  // Show only first two names in navbar if the full name is long
  const displayUserName = useMemo(() => {
    if (!userName) return null;
    const parts = String(userName).trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 2) return parts.join(" ");
    return `${parts[0]} ${parts[1]}`;
  }, [userName]);

  // Get first letter of user name for avatar
  const userInitial = useMemo(() => {
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    return null;
  }, [userName]);

  // Logout
  function LogOut() {
    clearToken();
    setAuthorization(null);
    localStorage.removeItem("isAdmin");

    // Ensure language is set to default (English) or saved preference
    const savedLang = localStorage.getItem("lang");
    const lang =
      savedLang && ["en", "ar", "ur"].includes(savedLang) ? savedLang : "ar";
    i18n.changeLanguage(lang);
    const dir = lang === "ar" || lang === "ur" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;

    navigate("/");
  }

  // Change Language
  function changeLang(lang) {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    localStorage.setItem("lang", lang);
    document.dir = lang === "ar" || lang === "ur" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    setShowLangMenu(false);
  }

  return (
    <nav className="main-navbar">
      <div className="navbar-inner">
        {/* Logo and Install App Button */}
        <div className="navbar-logo-section">
          <div className="navbar-logo">
            <img
              src="/mazraaOnlineLogo.png"
              alt="Logo"
              className="navbar-logo-img"
            />
            <span className="navbar-title" style={{ color: "#005f4b" }}>
              {t("app.title")}
            </span>
          </div>
          {/* Install App Button */}
          <div className="navbar-install-app">
            <InstallAppButton />
          </div>
        </div>

        {/* Links */}
        <div className="navbar-links">
          <Link to="/" className={location.pathname === "/" ? "active" : ""}>
            {t("navbar.home")}
          </Link>

          {/* ✅ Services يظهر فقط لو المستخدم عامل Login */}
          {Authorization && (
            <Link
              to="/homeServices"
              className={location.pathname === "/homeServices" ? "active" : ""}
            >
              {t("navbar.services")}
            </Link>
          )}

          <Link
            to="/listPlan"
            className={location.pathname === "/listPlan" ? "active" : ""}
          >
            {t("navbar.plans") || "Plans"}
          </Link>

          {isAdmin && (
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "active" : ""}
            >
              {t("navbar.dashboard")}
            </Link>
          )}
        </div>

        {/* Right: language + auth (desktop); on small screens language + hamburger stay grouped */}
        <div className="navbar-trailing">
          <div className="navbar-actions">
            {/* Language Dropdown */}
            <div className="language-dropdown">
              <button
                className="language-icon-btn"
                onClick={() => setShowLangMenu(!showLangMenu)}
              >
                <GrLanguage size={22} />
                <span className="language-current-label">
                  {t(`language.${language}`)}
                </span>
              </button>

              {showLangMenu && (
                <div className="language-menu">
                  <button onClick={() => changeLang("en")}>
                    {t("language.en")}
                  </button>
                  <button onClick={() => changeLang("ar")}>
                    {t("language.ar")}
                  </button>
                  <button onClick={() => changeLang("ur")}>
                    {t("language.ur")}
                  </button>
                </div>
              )}
            </div>

            <div className="navbar-auth-cluster">
              {/* Auth Buttons */}
              {Authorization ? (
                <button onClick={LogOut} className="navbar-login-btn">
                  {t("navbar.logout")}
                </button>
              ) : (
                <>
                  <Link to="/login" className="navbar-login-btn">
                    {t("navbar.signIn")}
                  </Link>
                  <Link to="/register" className="navbar-signup-btn">
                    {t("navbar.signUp")}
                  </Link>
                </>
              )}

              {/* User Name Display with Avatar - after logout, on the left in RTL */}
              {Authorization && displayUserName && (
                <Link to="/profile" className="navbar-user-name" title={userName}>
                  <div className="user-avatar">
                    <span className="user-avatar-initial">{userInitial}</span>
                  </div>
                  <span className="user-name-text">{displayUserName}</span>
                </Link>
              )}
            </div>
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          {/* User Name in Mobile Menu */}
          {Authorization && displayUserName && (
            <Link 
              to="/profile" 
              className="mobile-user-name"
              title={userName}
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="user-avatar">
                <span className="user-avatar-initial">{userInitial}</span>
              </div>
              <span className="user-name-text">{displayUserName}</span>
            </Link>
          )}

          <Link to="/" onClick={() => setMobileMenuOpen(false)}>
            {t("navbar.home")}
          </Link>

          {/* ✅ Services يظهر فقط لو المستخدم عامل Login */}
          {Authorization && (
            <Link to="/homeServices" onClick={() => setMobileMenuOpen(false)}>
              {t("navbar.services")}
            </Link>
          )}

          <Link to="/listPlan" onClick={() => setMobileMenuOpen(false)}>
            {t("navbar.plans") || "Plans"}
          </Link>

          {isAdmin && (
            <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
              {t("navbar.dashboard")}
            </Link>
          )}

          {/* Auth */}
          <div className="mobile-auth-buttons">
            {Authorization ? (
              <button
                onClick={() => {
                  LogOut();
                  setMobileMenuOpen(false);
                }}
                className="navbar-login-btn"
              >
                {t("navbar.logout")}
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="navbar-login-btn"
                >
                  {t("navbar.signIn")}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="navbar-signup-btn"
                >
                  {t("navbar.signUp")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
